import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useQuery } from 'react-query';
import { DataSourcesHooks } from '@frontend/api-data-sources';
import { PersonAPI, PersonsV3, PersonTypes } from '@frontend/api-person';
import { PaymentsPersonSearchContent } from '@frontend/patient-dropdown';
import { Photos } from '@frontend/photos';
import { theme } from '@frontend/theme';
import {
  Heading,
  Listbox,
  PopoverDialog,
  SearchField,
  Text,
  useDebouncedValue,
  useFormField,
  useListboxState,
  usePopoverDialog,
} from '@frontend/design-system';
import { UserIconContainer } from './user-icon-container';

type UserSelectorProps = {
  label: string;
  placeHolder: string;
  setSelected: (person: PersonTypes.Person) => void;
  locationId?: string;
  selectedUser?: PersonTypes.Person | null;
  trackingId?: string;
};
const API_RESPONSE_LIMIT = 50;
export const UserSelector = ({
  label,
  placeHolder,
  setSelected,
  locationId,
  selectedUser,
  trackingId,
}: UserSelectorProps) => {
  const listboxProps = useListboxState('');
  const searchField = useFormField({ type: 'text' });
  const searchValue = useDebouncedValue(searchField.value, API_RESPONSE_LIMIT);
  const { demoSourceIds } = DataSourcesHooks.useDemoLocationSourceIdsShallowStore('demoSourceIds');
  const { getTriggerProps, getDialogProps, close } = usePopoverDialog<HTMLElement>({
    placement: 'bottom-start',
  });

  const personsQuery = useQuery({
    queryKey: [locationId, 'payment-plan-person', searchValue, ...demoSourceIds],
    queryFn: () =>
      PersonAPI.getPersonsV3({
        order: [
          { orderBy: 'LAST_NAME', descending: false },
          { orderBy: 'FIRST_NAME', descending: false },
        ],
        statusFilter: 'Active',
        search: searchValue,
        locationId: locationId ?? '',
        sourceIds: demoSourceIds,
        responseLimit: 10,
      }),
    enabled: !!searchValue && !!locationId,
  });

  const persons = useMemo(() => {
    return personsQuery.data?.rows?.map((p) => PersonsV3.PersonHelpers.convertPersonV3ToPerson(p)) ?? [];
  }, [personsQuery]);

  return (
    <>
      <Text weight='bold' color='subdued'>
        {label}
      </Text>
      <div
        data-trackingid={`${trackingId}:trigger`}
        css={{
          display: 'flex',
          gap: theme.spacing(1),
          padding: theme.spacing(1),
          paddingLeft: selectedUser ? theme.spacing(0) : theme.spacing(1),
          width: 'fit-content',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.colors.neutral5,
          },
        }}
        {...getTriggerProps()}
      >
        {!selectedUser && <UserIconContainer width={32} height={32} />}
        <Heading
          level={2}
          css={{
            color: selectedUser ? theme.font.colors.default : theme.font.colors.subdued,
            borderBottom: selectedUser ? `1px solid ${theme.colors.neutral20}` : 'none',
          }}
        >
          {selectedUser ? <SelectedPersonUI selectedPerson={selectedUser} /> : placeHolder}
        </Heading>
      </div>
      <PopoverDialog {...getDialogProps()} returnFocus={false}>
        <SearchField
          data-trackingid={`${trackingId}:search-field`}
          name='search-user'
          {...searchField}
          onChange={(e) => {
            searchField.onChange(e);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        />
        <Listbox
          {...listboxProps}
          onSelect={(selectedValue) => {
            listboxProps.onSelect(selectedValue);
            const person = persons.find((p) => p.PersonID === selectedValue);
            if (person) setSelected(person);
            close();
          }}
          css={{
            marginTop: theme.spacing(1),
            maxHeight: '30vh',
            display: 'block',
          }}
        >
          {personsQuery.isLoading ? (
            <Listbox.Option key='loading' disabled value='loading'>
              Loading...
            </Listbox.Option>
          ) : null}
          {persons.map((person) => (
            <Listbox.Option key={person.PersonID} value={person.PersonID}>
              <PaymentsPersonSearchContent
                person={person}
                css={css`
                  display: flex;
                  gap: ${theme.spacing(2)};
                `}
              />
            </Listbox.Option>
          ))}
        </Listbox>
      </PopoverDialog>
    </>
  );
};

const SelectedPersonUI = ({ selectedPerson }: { selectedPerson: PersonTypes.Person | null }) => {
  if (!selectedPerson) return null;

  const personFullName = `${selectedPerson?.FirstName ?? ''} ${selectedPerson?.LastName ?? ''}`;
  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'row',
        gap: theme.spacing(1),
        alignItems: 'center',
        padding: theme.spacing(1, 0),
      }}
    >
      <Photos.ContactProfilePhoto personId={selectedPerson.PersonID} name={personFullName} size='small' disableClick />
      <Text textAlign='left'>{personFullName}</Text>
    </div>
  );
};
